import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { formatPhoneNumber } from "@/lib/utils";
import {
  useDeactivateUserMutation,
  useSendUserInviteMutation,
  useUpdateMoverMutation,
} from "@/store/mover/apis";
import { updateSelectedMover } from "@/store/mover/slice";
import { User } from "@/types";
import { SaveIcon, SendIcon, TrashIcon } from "lucide-react";

import React, { useState } from "react";
import { useDispatch } from "react-redux";

const TeamMember: React.FC<Partial<User>> = ({
  id,
  name,
  phone,
  email,
  roles,
  preferred_language,
  seen_at,
}) => {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const [updateMoverApi, { isLoading }] = useUpdateMoverMutation();
  const [deactivateUserApi] = useDeactivateUserMutation();
  const [sendUserInviteApi] = useSendUserInviteMutation();

  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [crewData, setCrewData] = useState({
    name,
    phone,
    email,
    roles,
    preferred_language,
  });

  const handleUpdateCrewMember = async (index: number) => {
    if (isLoading) {
      return;
    }

    try {
      const updatedMover = await updateMoverApi({
        data: {
          users_attributes: [
            {
              id: index,
              name: crewData.name,
              email: crewData.email,
              phone: crewData.phone,
              preferred_language: crewData.preferred_language,
              ...(crewData.email ? { email: crewData.email } : {}),
            },
          ],
        },
      }).unwrap();

      dispatch(updateSelectedMover(updatedMover));

      toast({
        title: "Success",
        description: "Crew updated successfully.",
      });
    } catch (error) {
      console.error("Error updating crew:", error);
      toast({
        title: "Error",
        description: "Failed to update crew. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleSendInvite = async (id: number) => {
    if (isLoading) {
      return;
    }

    try {
      await sendUserInviteApi({ user_id: id }).unwrap();

      toast({
        title: "Success",
        description: "Invitation sent!",
      });
    } catch (error) {
      console.error("Error sending invite:", error);
      toast({
        title: "Error",
        description: "Failed to send invite. Please try again.",
        variant: "destructive",
      });
    }
  };

  const deactivateAccount = async (id: number) => {
    if (isLoading) {
      return;
    }

    try {
      const updatedMover = await deactivateUserApi({
        user_id: id,
      }).unwrap();

      dispatch(
        updateSelectedMover({
          ...updatedMover,
          users: updatedMover.users.filter((user: User) => user.id !== id),
        })
      );

      toast({
        title: "Success",
        description: "User deactivated successfully.",
      });
    } catch (error) {
      console.error("Error deactivating user:", error);

      toast({
        title: "Error",
        description: "Failed to deactivate user. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (!id) {
    return null;
  }

  return (
    <div>
      <div className="gap-4 items-center grid grid-cols-12">
        <div className="col-span-2">
          <Input
            name={`crews[${id}].name`}
            value={crewData.name}
            placeholder="Name"
            onChange={(e) =>
              setCrewData((prev) => ({ ...prev, name: e.target.value }))
            }
          />
        </div>
        <div className="col-span-3">
          <Input
            name={`crews[${id}].email`}
            value={crewData.email}
            placeholder="Email"
            onChange={(e) =>
              setCrewData((prev) => ({ ...prev, email: e.target.value }))
            }
          />
        </div>
        <div className="col-span-2">
          <Input
            name={`crews[${id}].phone`}
            value={crewData.phone}
            placeholder="Phone"
            onChange={(e) =>
              setCrewData((prev) => ({
                ...prev,
                phone: formatPhoneNumber(e.target.value),
              }))
            }
          />
        </div>
        <div className="col-span-3 hidden">
          <div className="flex flex-row gap-2">
            <div className="flex items-baseline space-x-2">
              <Switch
                id="owner"
                checked={crewData.roles?.includes("owner")}
                onCheckedChange={(checked) =>
                  setCrewData((prev) => ({
                    ...prev,
                    roles: checked
                      ? [...(prev.roles || []), "owner"]
                      : (prev.roles || []).filter((role) => role !== "owner"),
                  }))
                }
              />
              <div>
                <label htmlFor="owner" className="font-medium text-sm">
                  Owner
                </label>
                {/* <div className="text-sm text-gray-800 flex items-center gap-1">
                  <CheckIcon className="w-4 h-4" />
                  Software
                </div>
                <div className="text-sm text-gray-800 flex items-center gap-1">
                  <CheckIcon className="w-4 h-4" />
                  Billing &amp; Payments
                </div>
                <div className="text-sm text-gray-800 flex items-center gap-1">
                  <CheckIcon className="w-4 h-4" />
                  Mobile app
                </div> */}
              </div>
            </div>

            <div className="flex items-baseline space-x-2">
              <Switch
                id="sales"
                checked={crewData.roles?.includes("sales")}
                onCheckedChange={(checked) =>
                  setCrewData((prev) => ({
                    ...prev,
                    roles: checked
                      ? [...(prev.roles || []), "sales"]
                      : (prev.roles || []).filter((role) => role !== "sales"),
                  }))
                }
              />
              <div>
                <label htmlFor="sales" className="font-medium text-sm">
                  Sales
                </label>
                {/* <div className="text-sm text-gray-800 flex items-center gap-1">
                  <CheckIcon className="w-4 h-4" />
                  Software
                </div>
                <div className="text-sm text-gray-800 flex items-center gap-1">
                  <CheckIcon className="w-4 h-4" />
                  Mobile app
                </div> */}
              </div>
            </div>

            <div className="flex items-baseline space-x-2">
              <Switch
                id="dispatcher"
                checked={crewData.roles?.includes("dispatcher")}
                onCheckedChange={(checked) =>
                  setCrewData((prev) => ({
                    ...prev,
                    roles: checked
                      ? [...(prev.roles || []), "dispatcher"]
                      : (prev.roles || []).filter(
                          (role) => role !== "dispatcher"
                        ),
                  }))
                }
              />
              <div>
                <label htmlFor="dispatcher" className="font-medium text-sm">
                  Dispatcher
                </label>
                {/* <div className="text-sm text-gray-800 flex items-center gap-1">
                  <CheckIcon className="w-4 h-4" />
                  Software
                </div>
                <div className="text-sm text-gray-800 flex items-center gap-1">
                  <CheckIcon className="w-4 h-4" />
                  Mobile app
                </div> */}
              </div>
            </div>

            <div className="flex items-baseline space-x-2">
              <Switch
                id="crew"
                checked={crewData.roles?.includes("crew")}
                onCheckedChange={(checked) =>
                  setCrewData((prev) => ({
                    ...prev,
                    roles: checked
                      ? [...(prev.roles || []), "crew"]
                      : (prev.roles || []).filter((role) => role !== "crew"),
                  }))
                }
              />
              <div>
                <label htmlFor="crew" className="font-medium text-sm">
                  Crew
                </label>
                {/* <div className="text-sm text-gray-800 flex items-center gap-1">
                  <CheckIcon className="w-4 h-4" />
                  Mobile app
                </div> */}
              </div>
            </div>
          </div>
        </div>
        <div className="col-span-5 flex items-center gap-1 ">
          <Button
            className="flex items-center gap-1 px-0 border-black px-2 text-black hover:decoration-yellow-500"
            variant="link"
            type="button"
            onClick={() => handleUpdateCrewMember(id)}
          >
            <SaveIcon className="w-3 h-3" />
            Save
          </Button>
          {!seen_at && (
            <Button
              type="button"
              className="text-blue-800 flex items-center gap-1"
              variant="link"
              onClick={() => handleSendInvite(id)}
            >
              <SendIcon className="w-3 h-3" />
              Re-send Invite
            </Button>
          )}
          <Button
            type="button"
            className="text-red-700 border-red-700 flex items-center gap-1"
            variant="link"
            onClick={() => setShowCancelConfirm(true)}
          >
            <TrashIcon className="w-3 h-3" />
            Deactivate
          </Button>
        </div>
        <AlertDialog
          open={showCancelConfirm}
          onOpenChange={setShowCancelConfirm}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Disable {name}'s access?</AlertDialogTitle>
              <AlertDialogDescription className="text-black">
                This will deactivate {name} from your account and remove their
                access. You can give them access again at any time.
                <br />
                <br />
                <strong>Please confirm that you want to do this.</strong>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-red-500 text-white hover:bg-red-500/90"
                onClick={() => deactivateAccount(id)}
              >
                Yes, remove {name}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default TeamMember;
